import { useTranslation } from '@/app/providers';
import { Skeleton } from '@/shared/components/skeleton';
import { cn } from '@/shared/lib/cn';

const CONSENT_ROW_COUNT = 4;

function LabelAndInputSkeleton({
  className,
  withSplitInput = false,
}: {
  className?: string;
  withSplitInput?: boolean;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Skeleton className="h-4 w-20 rounded-md" />
      {withSplitInput ? (
        <div className="flex h-12 items-center overflow-hidden rounded-xs border border-border bg-card">
          <Skeleton className="h-full w-28 shrink-0 rounded-none rounded-l-xs" />
          <Skeleton className="h-full min-w-0 flex-1 rounded-none rounded-r-xs" />
        </div>
      ) : (
        <Skeleton className="h-12 w-full rounded-xs" />
      )}
    </div>
  );
}

/**
 * Layout-shaped loading state for the public promotion form (`/prmfrm`).
 * Matches the split poster + form shell so Suspense and API load feel continuous.
 */
export function PublicPromoFormSkeleton() {
  const { t } = useTranslation();

  return (
    <main
      className="min-h-screen bg-content-bg lg:h-dvh lg:overflow-hidden"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('publicPromo.loading')}
    >
      <div className="lg:grid lg:h-full lg:grid-cols-[minmax(280px,42%)_minmax(0,1fr)]">
        <aside className="relative h-60 shrink-0 overflow-hidden bg-surface sm:h-72 lg:h-full">
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
          <div className="relative flex h-full flex-col justify-end gap-3 p-6 sm:p-8">
            <Skeleton
              variant="circular"
              className="h-14 w-14 bg-white/30 ring-2 ring-white/40"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Skeleton className="h-3 w-20 rounded-md bg-white/25" />
              <Skeleton className="h-5 w-10 rounded-full bg-white/35" />
            </div>
            <Skeleton className="h-8 w-48 max-w-[85%] rounded-md bg-white/40 sm:h-9 sm:w-56" />
            <Skeleton className="h-3 w-64 max-w-full rounded-md bg-white/20" />
            <Skeleton className="h-3 w-40 max-w-full rounded-md bg-white/15" />
          </div>
        </aside>

        <section className="flex min-h-0 flex-col bg-card lg:overflow-y-auto">
          <div className="mx-auto flex w-full max-w-lg flex-1 flex-col px-5 py-6 sm:px-8 lg:py-8">
            <div className="mb-4 flex justify-end">
              <Skeleton className="h-9 w-9 rounded-md" />
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-5">
              <div className="space-y-2">
                <Skeleton className="h-7 w-56 max-w-full rounded-md sm:h-8 sm:w-64" />
                <Skeleton className="h-4 w-72 max-w-full rounded-md" />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <LabelAndInputSkeleton
                  className="sm:col-span-2"
                  withSplitInput
                />
                <LabelAndInputSkeleton />
                <LabelAndInputSkeleton />
                <LabelAndInputSkeleton className="sm:col-span-2" />
                <LabelAndInputSkeleton />
                <LabelAndInputSkeleton />
              </div>

              <div className="space-y-3 rounded-xl border border-border bg-muted/20 p-4">
                <Skeleton className="h-4 w-64 max-w-full rounded-md" />
                {Array.from({ length: CONSENT_ROW_COUNT }, (_, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 shrink-0 rounded-sm" />
                    <Skeleton
                      className={cn(
                        'h-3.5 rounded-md',
                        index === 3 ? 'w-24' : 'w-52 max-w-[85%]',
                      )}
                    />
                  </div>
                ))}
              </div>

              <Skeleton className="mt-auto h-12 w-full rounded-xs" />
              <Skeleton className="mx-auto h-3 w-28 rounded-md" />
            </div>
          </div>
        </section>
      </div>
      <span className="sr-only">{t('publicPromo.loading')}</span>
    </main>
  );
}
